import { Fraction } from '@learnforge/demo-math/numeric';
import { type Expr, num, variable, add, mul, pow, func, equals, canonicalise } from './expr';
import { Polynomial } from './polynomial';
import { factor } from './factor';

export type EquationKind =
  | 'linear'
  | 'quadratic'
  | 'polynomial'
  | 'rational'
  | 'radical'
  | 'literal';

export type SolveStep =
  | { rule: 'add' | 'sub' | 'mul' | 'div'; value: Expr; lhs: Expr; rhs: Expr; description: string }
  | {
      rule: 'distribute' | 'collect-like-terms' | 'cross-multiply';
      lhs: Expr;
      rhs: Expr;
      description: string;
    }
  | {
      rule: 'square-both';
      lhs: Expr;
      rhs: Expr;
      description: string;
      warning: 'extraneous-may-occur';
    }
  | { rule: 'factor'; original: Expr; factored: Expr; description: string }
  | {
      rule: 'apply-quadratic-formula';
      a: Fraction;
      b: Fraction;
      c: Fraction;
      result: Expr[];
      description: string;
    }
  | { rule: 'isolate-variable'; varName: string; lhs: Expr; rhs: Expr; description: string }
  | { rule: 'extraneous-root-removed'; candidate: Expr; reason: string };

export interface SolveResult {
  kind: EquationKind;
  solutions: Expr[];
  steps: SolveStep[];
  domainRestrictions?: readonly Expr[];
}

export function solveEquation(
  lhs: Expr,
  rhs: Expr,
  varName: string,
  options?: { method?: 'auto' | 'factor' | 'complete-square' | 'formula' },
): SolveResult {
  const method = options?.method ?? 'auto';

  // Check for radicals (with varName inside)
  if (containsRadicalWithVar(lhs, varName) || containsRadicalWithVar(rhs, varName)) {
    return solveRadical(lhs, rhs, varName);
  }

  // Check for rationals (denominators with varName) BEFORE canonicalising
  const denominators = collectDenominators(lhs, rhs, varName);
  if (denominators.length > 0) {
    return solveRational(lhs, rhs, varName, denominators);
  }

  // Move everything to one side: D = lhs - rhs
  const diff = add(lhs, mul(num(-1), rhs));
  const diffCanonical = canonicalise(diff);

  // Try to parse as polynomial
  const poly = Polynomial.fromExpr(diffCanonical, varName);

  if (poly !== null) {
    const degree = poly.degree;

    if (degree <= 1) {
      return solveLinear(lhs, rhs, varName, poly);
    }
    if (degree === 2) {
      return solveQuadratic(lhs, rhs, varName, poly, method);
    }
    if (degree > 2) {
      return solvePolynomial(lhs, rhs, varName, poly);
    }
  }

  // If polynomial parsing failed, check if it's a literal equation (symbolic vars)
  const allVars = new Set<string>();
  collectVariables(lhs, allVars);
  collectVariables(rhs, allVars);

  if (allVars.has(varName) && allVars.size > 1) {
    // Multiple variables - literal equation
    return solveLiteral(lhs, rhs, varName);
  }

  // Fallback: no solutions
  return {
    kind: 'linear',
    solutions: [],
    steps: [],
  };
}

/**
 * Check if an Expr contains a radical (sqrt) with varName inside
 */
function containsRadicalWithVar(expr: Expr, varName: string): boolean {
  switch (expr.kind) {
    case 'num':
      return false;
    case 'var':
      return false;
    case 'add':
    case 'mul':
      return expr.children.some((c) => containsRadicalWithVar(c, varName));
    case 'pow':
      return (
        containsRadicalWithVar(expr.base, varName) || containsRadicalWithVar(expr.exp, varName)
      );
    case 'func':
      if (expr.name === 'sqrt') {
        // Check if any arg contains the variable
        return expr.args.some((a) => containsVar(a, varName));
      }
      return expr.args.some((a) => containsRadicalWithVar(a, varName));
    case 'sigma':
      return (
        containsRadicalWithVar(expr.from, varName) ||
        containsRadicalWithVar(expr.to, varName) ||
        containsRadicalWithVar(expr.body, varName)
      );
  }
}

/**
 * Collect all Exprs that appear in a denominator (pow with negative exponent)
 */
function collectDenominators(lhs: Expr, rhs: Expr, varName: string): Expr[] {
  const denoms: Expr[] = [];
  const seen = new Set<string>();

  function visit(expr: Expr): void {
    switch (expr.kind) {
      case 'num':
      case 'var':
        break;
      case 'add': {
        for (const child of expr.children) {
          visit(child);
        }
        break;
      }
      case 'mul': {
        for (const child of expr.children) {
          visit(child);
        }
        break;
      }
      case 'pow': {
        visit(expr.base);
        visit(expr.exp);
        // Check if exponent is a negative number and base contains varName
        if (expr.exp.kind === 'num' && expr.exp.value.num < 0) {
          const baseStr = JSON.stringify(expr.base);
          if (!seen.has(baseStr) && containsVar(expr.base, varName)) {
            denoms.push(expr.base);
            seen.add(baseStr);
          }
        }
        break;
      }
      case 'func': {
        for (const arg of expr.args) {
          visit(arg);
        }
        break;
      }
      case 'sigma': {
        visit(expr.from);
        visit(expr.to);
        visit(expr.body);
        break;
      }
    }
  }

  visit(lhs);
  visit(rhs);
  return denoms;
}

/**
 * Check if an Expr contains a variable
 */
function containsVar(expr: Expr, varName: string): boolean {
  switch (expr.kind) {
    case 'num':
      return false;
    case 'var':
      return expr.name === varName;
    case 'add':
    case 'mul':
      return expr.children.some((c) => containsVar(c, varName));
    case 'pow':
      return containsVar(expr.base, varName) || containsVar(expr.exp, varName);
    case 'func':
      return expr.args.some((a) => containsVar(a, varName));
    case 'sigma':
      return (
        containsVar(expr.from, varName) ||
        containsVar(expr.to, varName) ||
        containsVar(expr.body, varName)
      );
  }
}

/**
 * Collect all variables in an expression
 */
function collectVariables(expr: Expr, vars: Set<string>): void {
  switch (expr.kind) {
    case 'num':
      break;
    case 'var':
      vars.add(expr.name);
      break;
    case 'add':
    case 'mul':
      for (const child of expr.children) {
        collectVariables(child, vars);
      }
      break;
    case 'pow':
      collectVariables(expr.base, vars);
      collectVariables(expr.exp, vars);
      break;
    case 'func':
      for (const arg of expr.args) {
        collectVariables(arg, vars);
      }
      break;
    case 'sigma':
      collectVariables(expr.from, vars);
      collectVariables(expr.to, vars);
      collectVariables(expr.body, vars);
      break;
  }
}

/**
 * Solve linear equation: a·x + b = 0
 */
function solveLinear(lhs: Expr, rhs: Expr, varName: string, _poly: Polynomial): SolveResult {
  const steps: SolveStep[] = [];

  // Check if we need to emit distribute step
  if (containsDistribution(lhs) || containsDistribution(rhs)) {
    steps.push({
      rule: 'distribute',
      lhs,
      rhs,
      description: 'step.distribute',
    });
  }

  // Move to standard form: a·x + b = 0
  const diff = add(lhs, mul(num(-1), rhs));
  const diffCanonical = canonicalise(diff);

  const polyStd = Polynomial.fromExpr(diffCanonical, varName);
  if (polyStd === null || polyStd.degree !== 1) {
    return {
      kind: 'linear',
      solutions: [],
      steps: [],
    };
  }

  // polyStd.coeffs = [b, a] where equation is a·x + b = 0
  const b = polyStd.coeffs[0] ?? Fraction.ZERO;
  const a = polyStd.coeffs[1] ?? Fraction.ONE;

  if (a.num === 0) {
    // No variable term
    return {
      kind: 'linear',
      solutions: [],
      steps,
    };
  }

  // Solution: x = -b/a
  const solution = num(b.neg().div(a));

  // Emit subtract step (moving constant to other side)
  if (b.num !== 0) {
    steps.push({
      rule: 'sub',
      value: rhs,
      lhs,
      rhs,
      description: 'step.subtract',
    });
  }

  // Emit divide step (dividing by coefficient)
  if (!a.equals(Fraction.ONE)) {
    steps.push({
      rule: 'div',
      value: num(a),
      lhs: add(mul(num(a), variable(varName)), num(b)),
      rhs: num(0),
      description: 'step.divide',
    });
  }

  return {
    kind: 'linear',
    solutions: [solution],
    steps,
  };
}

/**
 * Check if an Expr visibly contains a distribution (mul with add child)
 */
function containsDistribution(expr: Expr): boolean {
  switch (expr.kind) {
    case 'num':
    case 'var':
      return false;
    case 'mul': {
      for (const child of expr.children) {
        if (child.kind === 'add') return true;
        if (containsDistribution(child)) return true;
      }
      return false;
    }
    case 'add':
    case 'pow':
    case 'func':
    case 'sigma':
      return false;
  }
}

/**
 * Solve quadratic equation: a·x² + b·x + c = 0
 */
function solveQuadratic(
  _lhs: Expr,
  _rhs: Expr,
  _varName: string,
  poly: Polynomial,
  method: string,
): SolveResult {
  const steps: SolveStep[] = [];

  // Get standard form coefficients
  const c = poly.coeffs[0] ?? Fraction.ZERO;
  const b = poly.coeffs[1] ?? Fraction.ZERO;
  const a = poly.coeffs[2] ?? Fraction.ONE;

  // Try to factor if method is 'factor' or 'auto'
  if (method === 'factor' || method === 'auto') {
    const factorResult = factor(poly);
    if (canExtractRationalRoots(factorResult) && (method === 'factor' || method === 'auto')) {
      const roots = extractRationalRoots(factorResult);
      if (roots.length > 0 && (method === 'factor' || method === 'auto')) {
        // Emit factor step
        const factored = buildFactoredExpr(factorResult);
        steps.push({
          rule: 'factor',
          original: _lhs,
          factored,
          description: 'step.factor',
        });
        return {
          kind: 'quadratic',
          solutions: roots.map((r) => num(r)),
          steps,
        };
      }
    }
  }

  // Use quadratic formula
  const delta = b.mul(b).sub(a.mul(c).mul(new Fraction(4, 1)));
  const result: Expr[] = [];

  // Check if delta is a perfect square
  if (isPerfectSquare(delta)) {
    const sqrtDelta = sqrtFraction(delta)!;
    const root1 = b
      .neg()
      .add(sqrtDelta)
      .div(a.mul(new Fraction(2, 1)));
    const root2 = b
      .neg()
      .sub(sqrtDelta)
      .div(a.mul(new Fraction(2, 1)));
    result.push(num(root1), num(root2));
  } else {
    // Return radical forms
    // For simplicity with b=0 case: x = ±sqrt(-c/a)
    if (b.num === 0) {
      const radicand = c.neg().div(a);
      const sqrtExpr = func('sqrt', num(radicand));
      result.push(sqrtExpr, mul(num(-1), sqrtExpr));
    } else {
      // General case: use (-b ± sqrt(Δ))/(2a)
      const sqrtDelta = func('sqrt', num(delta));
      const denom = num(a.mul(new Fraction(2, 1)));
      result.push(
        mul(add(num(b.neg()), sqrtDelta), pow(denom, num(-1))),
        mul(add(num(b.neg()), mul(num(-1), sqrtDelta)), pow(denom, num(-1))),
      );
    }
  }

  steps.push({
    rule: 'apply-quadratic-formula',
    a,
    b,
    c,
    result,
    description: 'step.quadratic-formula',
  });

  return {
    kind: 'quadratic',
    solutions: result,
    steps,
  };
}

/**
 * Check if a Fraction is a perfect square (both num and den are perfect squares)
 */
function isPerfectSquare(f: Fraction): boolean {
  const sqrtNum = Math.sqrt(Math.abs(f.num));
  const sqrtDen = Math.sqrt(f.den);
  return Number.isInteger(sqrtNum) && Number.isInteger(sqrtDen) && f.sign >= 0;
}

/**
 * Get the square root of a perfect square Fraction
 */
function sqrtFraction(f: Fraction): Fraction | null {
  if (!isPerfectSquare(f)) return null;
  const sqrtNum = Math.sqrt(f.num);
  const sqrtDen = Math.sqrt(f.den);
  return new Fraction(sqrtNum, sqrtDen);
}

/**
 * Check if a FactorResult yields rational roots
 */
function canExtractRationalRoots(result: ReturnType<typeof factor>): boolean {
  for (const fac of result.factors) {
    if (fac.factor.degree !== 1) {
      return false;
    }
  }
  return true;
}

/**
 * Extract rational roots from a factored quadratic
 */
function extractRationalRoots(result: ReturnType<typeof factor>): Fraction[] {
  const roots: Fraction[] = [];
  for (const fac of result.factors) {
    if (fac.factor.degree === 1) {
      // Linear factor: (px + q) = 0 => x = -q/p
      const p = fac.factor.coeffs[1] ?? Fraction.ZERO;
      const q = fac.factor.coeffs[0] ?? Fraction.ZERO;
      if (p.num !== 0) {
        const root = q.neg().div(p);
        for (let i = 0; i < fac.multiplicity; i += 1) {
          roots.push(root);
        }
      }
    }
  }
  return roots;
}

/**
 * Build a factored expression from a FactorResult
 */
function buildFactoredExpr(result: ReturnType<typeof factor>): Expr {
  let expr: Expr = num(result.content);
  for (const fac of result.factors) {
    for (let i = 0; i < fac.multiplicity; i += 1) {
      expr = mul(expr, fac.factor.toExpr());
    }
  }
  return expr;
}

/**
 * Solve polynomial equation (degree > 2) via factoring
 */
function solvePolynomial(_lhs: Expr, _rhs: Expr, _varName: string, poly: Polynomial): SolveResult {
  const steps: SolveStep[] = [];
  const factorResult = factor(poly);

  // Extract roots from linear factors
  const roots: Expr[] = [];
  for (const fac of factorResult.factors) {
    if (fac.factor.degree === 1) {
      const p = fac.factor.coeffs[1] ?? Fraction.ZERO;
      const q = fac.factor.coeffs[0] ?? Fraction.ZERO;
      if (p.num !== 0) {
        const root = q.neg().div(p);
        roots.push(num(root));
      }
    }
  }

  if (roots.length > 0) {
    const factored = buildFactoredExpr(factorResult);
    steps.push({
      rule: 'factor',
      original: _lhs,
      factored,
      description: 'step.factor',
    });
  }

  return {
    kind: 'polynomial',
    solutions: roots,
    steps,
  };
}

/**
 * Solve rational equation: clear denominators and solve the resulting polynomial
 */
function solveRational(lhs: Expr, rhs: Expr, varName: string, denominators: Expr[]): SolveResult {
  const steps: SolveStep[] = [];

  // Find the zeros of each denominator for domain restrictions
  // For denom = x - 2, solve x - 2 = 0 to get x = 2
  const domainRestrictions: Expr[] = [];
  for (const denom of denominators) {
    const denomZeros = solveEquation(denom, num(0), varName);
    for (const zero of denomZeros.solutions) {
      domainRestrictions.push(zero);
    }
  }

  // Multiply both sides by the first denominator to clear it
  // For (x+1)/(x-2) = 3, multiply by (x-2)
  const lcd = denominators[0]!;
  steps.push({
    rule: 'mul',
    value: lcd,
    lhs,
    rhs,
    description: 'step.multiply-by-denominator',
  });

  // Multiply both sides by lcd
  const newLhs = mul(lhs, lcd);
  const newRhs = mul(rhs, lcd);

  // Simplify by removing pow(base, -1) when base is also a direct factor
  const simplifiedLhs = simplifyNegativePowers(newLhs, denominators);
  const simplifiedRhs = canonicalise(newRhs);

  // Now solve: simplifiedLhs = simplifiedRhs
  const diff = add(simplifiedLhs, mul(num(-1), simplifiedRhs));
  const diffCanonical = canonicalise(diff);

  const poly = Polynomial.fromExpr(diffCanonical, varName);
  if (poly === null) {
    return {
      kind: 'rational',
      solutions: [],
      steps,
      domainRestrictions,
    };
  }

  // Solve the polynomial and filter out domain restrictions
  const allSolutions: Expr[] = [];
  if (poly.degree <= 1) {
    const linResult = solveLinear(simplifiedLhs, simplifiedRhs, varName, poly);
    allSolutions.push(...linResult.solutions);
  } else if (poly.degree === 2) {
    const quadResult = solveQuadratic(simplifiedLhs, simplifiedRhs, varName, poly, 'auto');
    allSolutions.push(...quadResult.solutions);
  } else if (poly.degree > 2) {
    const polyResult = solvePolynomial(simplifiedLhs, simplifiedRhs, varName, poly);
    allSolutions.push(...polyResult.solutions);
  }

  // Filter out domain restrictions
  const solutions: Expr[] = [];
  for (const sol of allSolutions) {
    if (!isInSet(sol, domainRestrictions)) {
      solutions.push(sol);
    }
  }

  return {
    kind: 'rational',
    solutions,
    steps,
    domainRestrictions,
  };
}

/**
 * Simplify an expression by canceling pow(denom, -1) with denom base terms
 */
function simplifyNegativePowers(expr: Expr, denominators: Expr[]): Expr {
  const canonical = canonicalise(expr);

  // If it's a mul, find pow(d, -1) terms and their matching d terms, and remove both
  if (canonical.kind === 'mul') {
    // Build a map of which denominators exist
    const denomMap = new Map<string, Expr>();
    for (const denom of denominators) {
      const denomCanonical = canonicalise(denom);
      denomMap.set(JSON.stringify(denomCanonical), denomCanonical);
    }

    const removed = new Set<number>();

    // Find pow(d, -1) terms and mark both the pow and the matching base for removal
    for (let i = 0; i < canonical.children.length; i += 1) {
      const child = canonical.children[i]!;
      if (child.kind === 'pow' && child.exp.kind === 'num' && child.exp.value.num < 0) {
        const baseCanonical = canonicalise(child.base);
        const baseStr = JSON.stringify(baseCanonical);
        if (denomMap.has(baseStr)) {
          // Mark the pow term for removal
          removed.add(i);

          // Also find and mark the matching base term for removal
          for (let j = 0; j < canonical.children.length; j += 1) {
            if (j !== i && !removed.has(j)) {
              const otherChild = canonical.children[j]!;
              const otherStr = JSON.stringify(canonicalise(otherChild));
              if (otherStr === baseStr) {
                removed.add(j);
                break; // Remove only one matching base
              }
            }
          }
        }
      }
    }

    // Collect non-removed children
    const filtered: Expr[] = [];
    for (let i = 0; i < canonical.children.length; i += 1) {
      if (!removed.has(i)) {
        filtered.push(canonical.children[i]!);
      }
    }

    if (filtered.length === 0) {
      return num(1);
    }
    if (filtered.length === 1) {
      return filtered[0]!;
    }
    return mul(...filtered);
  }

  return canonical;
}

/**
 * Check if an Expr equals any in a set (via canonicalise)
 */
function isInSet(expr: Expr, set: Expr[]): boolean {
  const canonical = canonicalise(expr);
  for (const item of set) {
    if (equals(canonical, canonicalise(item))) {
      return true;
    }
  }
  return false;
}

/**
 * Solve radical equation: isolate, square, solve, verify
 */
function solveRadical(lhs: Expr, rhs: Expr, varName: string): SolveResult {
  const steps: SolveStep[] = [];

  // Emit square-both step
  steps.push({
    rule: 'square-both',
    lhs,
    rhs,
    description: 'step.square-both',
    warning: 'extraneous-may-occur',
  });

  // Square both sides: (lhs)^2 = (rhs)^2
  // We need to handle radicals specially. If lhs = sqrt(u), then lhs^2 = u
  const simplifiedLhs = unsqrtRadical(lhs);
  const simplifiedRhs = mul(rhs, rhs);

  // Now solve simplifiedLhs = simplifiedRhs
  const diff = add(simplifiedLhs, mul(num(-1), simplifiedRhs));
  const diffCanonical = canonicalise(diff);
  const poly = Polynomial.fromExpr(diffCanonical, varName);

  const candidates: Expr[] = [];
  if (poly !== null) {
    if (poly.degree <= 1) {
      const linResult = solveLinear(simplifiedLhs, simplifiedRhs, varName, poly);
      candidates.push(...linResult.solutions);
    } else if (poly.degree === 2) {
      const quadResult = solveQuadratic(simplifiedLhs, simplifiedRhs, varName, poly, 'auto');
      candidates.push(...quadResult.solutions);
    } else if (poly.degree > 2) {
      const polyResult = solvePolynomial(simplifiedLhs, simplifiedRhs, varName, poly);
      candidates.push(...polyResult.solutions);
    }
  }

  // Verify each candidate by substituting into the original equation
  const verified: Expr[] = [];
  for (const candidate of candidates) {
    if (verifySolution(lhs, rhs, varName, candidate)) {
      verified.push(candidate);
    } else {
      steps.push({
        rule: 'extraneous-root-removed',
        candidate,
        reason: 'step.does-not-satisfy-original',
      });
    }
  }

  return {
    kind: 'radical',
    solutions: verified,
    steps,
  };
}

/**
 * Remove sqrt from a radical expression (assuming it's a single sqrt at top level)
 * sqrt(x+3)^2 = x+3
 */
function unsqrtRadical(expr: Expr): Expr {
  const canonical = canonicalise(expr);
  if (canonical.kind === 'func' && canonical.name === 'sqrt' && canonical.args.length === 1) {
    return canonical.args[0]!;
  }
  return expr;
}

/**
 * Verify a candidate solution by substituting into the original equation
 */
function verifySolution(lhs: Expr, rhs: Expr, varName: string, candidate: Expr): boolean {
  const lhsValue = evalExpr(lhs, varName, candidate);
  const rhsValue = evalExpr(rhs, varName, candidate);

  if (lhsValue === null || rhsValue === null) {
    return false;
  }

  return lhsValue.equals(rhsValue);
}

/**
 * Evaluate an Expr at a given value (substituting varName)
 */
function evalExpr(expr: Expr, varName: string, value: Expr): Fraction | null {
  const substituted = substitute(expr, varName, value);
  return numericValue(substituted);
}

/**
 * Substitute a variable in an Expr with another Expr
 */
function substitute(expr: Expr, varName: string, value: Expr): Expr {
  switch (expr.kind) {
    case 'num':
      return expr;
    case 'var':
      return expr.name === varName ? value : expr;
    case 'add':
      return add(...expr.children.map((c) => substitute(c, varName, value)));
    case 'mul':
      return mul(...expr.children.map((c) => substitute(c, varName, value)));
    case 'pow':
      return pow(substitute(expr.base, varName, value), substitute(expr.exp, varName, value));
    case 'func':
      return func(expr.name, ...expr.args.map((a) => substitute(a, varName, value)));
    case 'sigma':
      return expr; // Simplified: don't handle sigma
  }
}

/**
 * Extract numeric value from an Expr (must be fully evaluated to a number)
 */
function numericValue(expr: Expr): Fraction | null {
  const canonical = canonicalise(expr);
  switch (canonical.kind) {
    case 'num':
      return canonical.value;
    case 'add': {
      // If all children are numeric, sum them
      let sum = Fraction.ZERO;
      for (const child of canonical.children) {
        if (child.kind !== 'num') return null;
        sum = sum.add(child.value);
      }
      return sum;
    }
    case 'mul': {
      // If all children are numeric, multiply them
      let prod = Fraction.ONE;
      for (const child of canonical.children) {
        if (child.kind !== 'num') return null;
        prod = prod.mul(child.value);
      }
      return prod;
    }
    case 'pow': {
      const base = numericValue(canonical.base);
      const exp = numericValue(canonical.exp);
      if (base === null || exp === null) return null;
      if (!exp.isInteger()) return null;
      const expInt = exp.num / exp.den;
      const magnitude = Math.abs(expInt);
      let result = Fraction.ONE;
      for (let i = 0; i < magnitude; i += 1) {
        result = result.mul(base);
      }
      if (expInt < 0) {
        if (result.num === 0) return null; // 0^(negative) is undefined
        return result.reciprocal();
      }
      return result;
    }
    case 'func': {
      if (canonical.name === 'sqrt' && canonical.args.length === 1) {
        const radicand = numericValue(canonical.args[0]!);
        if (radicand === null) return null;
        if (radicand.sign < 0) return null;
        const sqrtNum = Math.sqrt(radicand.num);
        const sqrtDen = Math.sqrt(radicand.den);
        if (!Number.isInteger(sqrtNum) || !Number.isInteger(sqrtDen)) {
          return null;
        }
        return new Fraction(sqrtNum, sqrtDen);
      }
      return null;
    }
    default:
      return null;
  }
}

/**
 * Solve literal equation (symbolic vars, isolate target)
 */
function solveLiteral(lhs: Expr, rhs: Expr, varName: string): SolveResult {
  const steps: SolveStep[] = [];

  // Rearrange to isolate varName on one side
  // For F = m*a, solve for m: divide both sides by a => m = F/a
  // This is a simplified approach: just divide rhs by all factors not containing varName

  // Collect factors in rhs that don't contain varName
  const rhsFactors = collectFactors(rhs);
  let divisor: Expr | null = null;

  for (const factor of rhsFactors) {
    if (!containsVar(factor, varName)) {
      divisor = factor;
      break;
    }
  }

  if (divisor !== null) {
    steps.push({
      rule: 'div',
      value: divisor,
      lhs,
      rhs,
      description: 'step.divide',
    });

    const solution = mul(lhs, pow(divisor, num(-1)));
    return {
      kind: 'literal',
      solutions: [solution],
      steps,
    };
  }

  // Fallback: return lhs as solution
  return {
    kind: 'literal',
    solutions: [lhs],
    steps,
  };
}

/**
 * Collect factors from a multiplication
 */
function collectFactors(expr: Expr): Expr[] {
  const canonical = canonicalise(expr);
  if (canonical.kind === 'mul') {
    return canonical.children;
  }
  return [canonical];
}
